// Browser port of the import engine (stops-import/src/engine.js) with UI
// progress callbacks and the IndexedDB journal. The parsing/validation logic
// itself is the shared code in ./engine/ — byte-identical to the CLI's.

import { importApi, NetworkUncertainError } from "./importApi";
import { journal } from "./journal";

// Shared engine core (plain JS, allowJs — ESM syntax; CJS module.exports breaks
// under CRA's production webpack once babel injects its ESM helper imports)
// @ts-ignore — untyped shared JS
import { buildStopPayload } from "./engine/validate";
// @ts-ignore
import { looksLikeMp3 } from "./engine/parseZips";
// @ts-ignore
import { CDN_URL, LANG_ORDER } from "./engine/constants";
// @ts-ignore
import * as addLangEngine from "./engine/addLanguages";

// Typed facade over the untyped shared JS (its JSDoc is too loose for strict TS).
const planAddLanguages = addLangEngine.planAddLanguages as unknown as (model: any, serverStops: any[]) => AddLangPlan;
const mergeLanguageMap = addLangEngine.mergeLanguageMap as unknown as (existingMap: any, additions: Record<string, string>) => any[];
const buildUpdatePayload = addLangEngine.buildUpdatePayload as unknown as (tourId: number, mergedMap: any[]) => any;
const presentLangs = addLangEngine.presentLangs as unknown as (map: any) => string[];

export interface Progress {
  phase: "reconcile" | "stop" | "tail" | "verify" | "done";
  stopSl?: number;
  stopName?: string;
  stopsDone: number;
  stopsTotal: number;
  detail: string;
  uploadPct?: number;
}

export interface RunResult {
  created: number[];
  skipped: number[];
  uploads: number;
  tail: { route?: boolean; detail?: boolean; offline?: boolean };
  verify: VerifyResult | null;
}

export interface VerifyResult {
  ok: boolean;
  checked: number;
  totalFiles: number;
  failed: string[];
  failedNonTranscribe: string[];
}

function fname(tourId: number, sl: number, suffix: string) {
  return `t${tourId}_s${String(sl).padStart(2, "0")}${suffix}`;
}

function stopMatcher(stop: any) {
  return (sp: any) => {
    try {
      const names = typeof sp.name === "string" ? JSON.parse(sp.name) : sp.name;
      const enName = (names || []).find((n: any) => n.language === "en")?.text;
      return enName === stop.names.en && Math.abs(parseFloat(sp.lattitude) - stop.lat) < 0.0005;
    } catch {
      return false;
    }
  };
}

/** Read-oriented preflight — mirrors the CLI's engine.preflight. */
export async function preflight(tourId: number, model: any) {
  const issues: any[] = [];
  const push = (severity: string, code: string, message: string) => issues.push({ severity, code, message });

  const all = await importApi.getAllTours();
  const listed = (all || []).find((t: any) => t.id === tourId);
  if (!listed) {
    push("blocker", "tour-not-found", `Tour ${tourId} not found`);
    return { tour: null, issues };
  }
  const tour = await importApi.getTour(tourId);
  if (!tour?.id) {
    push("blocker", "tour-not-found", `Tour ${tourId} not found`);
    return { tour: null, issues };
  }
  const existing = tour.tourPointsDetail || [];
  const resumable = await journal.hasActive(tourId);
  if (existing.length > 0 && !resumable) {
    push("blocker", "tour-not-empty", `Tour already has ${existing.length} stops — the importer only fills empty tours`);
  } else if (existing.length > 0) {
    push("warning", "tour-partial", `Tour has ${existing.length} stops and an unfinished import journal — this run will RESUME (existing stops are kept, missing ones created)`);
  }
  if (!tour.imageUrl) push("warning", "tour-no-image", "Tour has no imageUrl — the app's detail hero and Home cards will be blank");
  if (!tour.thumbnailUrl) push("warning", "tour-no-thumb", "Tour has no thumbnailUrl — city cards/checkout will be blank");
  if (tour.published !== 0 && tour.published !== false) {
    push("warning", "tour-published-state", `Tour published = ${tour.published} — keep it unpublished (0) until you have reviewed the imported stops`);
  }
  const myBokun = tour.bokunId;
  if (myBokun && myBokun !== 0) {
    const clash = (all || []).find((t: any) => t.id !== tour.id && t.bokunId === myBokun);
    if (clash) push("warning", "bokunid-collision", `Tour's bokunId ${myBokun} is also on tour ${clash.id} — the Bokun cron matches by bokunId and could serve the wrong tour`);
  }
  try {
    const tpl = typeof tour.bookingEmailTemplate === "string" ? JSON.parse(tour.bookingEmailTemplate) : tour.bookingEmailTemplate;
    if (!Array.isArray(tpl) || tpl.length === 0) {
      push("warning", "no-email-template", "Tour has no booking email template yet — Bokun redeem emails for this tour would fail. Add it in Edit Tour before going live.");
    } else {
      const langs = tpl.map((v: any) => v.language);
      for (const l of model.languages) {
        if (!langs.includes(l)) push("warning", "template-missing-lang", `Booking email template has no "${l}" variant`);
      }
      for (const v of tpl) {
        // eslint-disable-next-line no-template-curly-in-string
        if (v.text && !v.text.includes("${redeemCode}")) push("warning", "template-no-redeemcode", `Email template (${v.language}) does not contain the redeem-code placeholder`);
      }
    }
  } catch {
    push("warning", "template-unparseable", "Tour's booking email template is not valid JSON — check it in Edit Tour");
  }
  return { tour, issues };
}

/**
 * Execute the import. Validation must have passed and the operator confirmed.
 * Sequential by design: DB stop order = id ASC.
 */
export async function runImport(
  model: any,
  tourId: number,
  onProgress: (p: Progress) => void,
  shouldAbort: () => boolean
): Promise<RunResult> {
  const result: RunResult = { created: [], skipped: [], uploads: 0, tail: {}, verify: null };
  const total = model.stops.length;
  const report = (p: Partial<Progress>) =>
    onProgress({ phase: "stop", stopsDone: result.created.length + result.skipped.length, stopsTotal: total, detail: "", ...p } as Progress);

  const tour = await importApi.getTour(tourId);
  if (!tour?.id) throw new Error(`Tour ${tourId} not found`);
  const placeId = tour.placeId;
  const serverStops = tour.tourPointsDetail || [];
  const resuming = await journal.hasActive(tourId);
  if (serverStops.length > 0 && !resuming) {
    throw new Error(`Tour already has ${serverStops.length} stops and there is no import journal for it — refusing to continue.`);
  }

  // Server reconciliation (resume): consume matches in SL order vs id-ASC rows.
  // NEVER delete a duplicate (tourPoints/delete erases shared Spaces audio).
  const doneSl = new Set<number>();
  if (resuming) {
    report({ phase: "reconcile", detail: "Checking which stops already exist…" });
    const pool = [...serverStops].sort((a: any, b: any) => a.id - b.id);
    for (const s of model.stops) {
      const idx = pool.findIndex(stopMatcher(s));
      if (idx !== -1) {
        doneSl.add(s.sl);
        pool.splice(idx, 1);
      }
    }
  }

  // Journal replay (resume only) + CDN liveness check on reused filenames.
  let journalUploads: Record<number, { image: string | null; audio: Record<string, string> }> = {};
  if (resuming) {
    journalUploads = (await journal.summarize(tourId)).uploads;
    for (const [sl, u] of Object.entries(journalUploads)) {
      if (u.image && !(await importApi.headOk(`${CDN_URL}/imageUpload/${u.image}`))) u.image = null;
      for (const [lang, f] of Object.entries(u.audio)) {
        if (f && !(await importApi.headOk(`${CDN_URL}/audioUpload/${f}`))) delete u.audio[lang];
      }
      void sl;
    }
  }

  const audioLangs: string[] = model.audioLangs || model.languages;
  const langOrder: string[] = LANG_ORDER.filter((l: string) => audioLangs.includes(l));

  for (const stop of model.stops) {
    if (shouldAbort()) throw new Error("Paused by operator — press Resume to continue (nothing is lost).");
    if (doneSl.has(stop.sl)) {
      result.skipped.push(stop.sl);
      continue;
    }
    const prior = journalUploads[stop.sl] || { image: null, audio: {} };

    // 1. image
    let imageFile = prior.image;
    if (!imageFile) {
      report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Uploading image…` });
      imageFile = await importApi.uploadImage(fname(tourId, stop.sl, `.${stop.image.ext}`), stop.image.data, (pct) =>
        report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Uploading image…`, uploadPct: pct })
      );
      await journal.append(tourId, { type: "upload", sl: stop.sl, kind: "image", file: imageFile });
      result.uploads++;
    }
    // 2. audio per selected language (lazy-extracted one at a time)
    const audioFiles: Record<string, string> = { ...prior.audio };
    for (const lang of langOrder) {
      if (audioFiles[lang]) continue;
      if (shouldAbort()) throw new Error("Paused by operator — press Resume to continue (nothing is lost).");
      const f = stop.audio[lang];
      const data = f.getData();
      if (!looksLikeMp3(data)) throw new Error(`Stop ${stop.sl} ${lang}: "${f.entryName}" is not a valid MP3 — fix the zip, then Resume.`);
      report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Uploading ${lang} audio (${(data.length / 1e6).toFixed(1)} MB)…` });
      const serverName = await importApi.uploadAudio(fname(tourId, stop.sl, `_${lang}.mp3`), data, (pct) =>
        report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Uploading ${lang} audio…`, uploadPct: pct })
      );
      await journal.append(tourId, { type: "upload", sl: stop.sl, kind: "audio", lang, file: serverName });
      audioFiles[lang] = serverName;
      result.uploads++;
    }
    // 3. create the stop with EXACTLY the server-returned filenames.
    report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Creating stop…` });
    const payload = buildStopPayload(stop, model.languages, audioLangs, tourId, placeId, { image: imageFile, audio: audioFiles });
    let created: any;
    try {
      created = await importApi.createStop(payload);
    } catch (e) {
      if (!(e instanceof NetworkUncertainError)) throw e;
      // Uncertain outcome: check the server before any resend (a blind resend
      // could duplicate the stop — the one unrecoverable failure mode).
      const fresh = await importApi.getTour(tourId);
      const landed = (fresh.tourPointsDetail || []).some(stopMatcher(stop));
      created = landed ? null : await importApi.createStop(payload);
    }
    await journal.append(tourId, { type: "stop_created", sl: stop.sl, id: created?.id ?? null });
    result.created.push(stop.sl);
    report({ stopSl: stop.sl, stopName: stop.names.en, detail: `Stop created.` });
  }

  // Tail: ALWAYS route -> detail -> offline, on every run and resume (the detail
  // file snapshots tourDirectionUrl, so route must come first).
  onProgress({ phase: "tail", stopsDone: total, stopsTotal: total, detail: "Generating walking route (Mapbox)…" });
  await importApi.generateRoute(tourId);
  result.tail.route = true;
  onProgress({ phase: "tail", stopsDone: total, stopsTotal: total, detail: "Refreshing tour detail file…" });
  await importApi.getTour(tourId); // designed side effect: rewrites tour-details-{id}.json
  result.tail.detail = true;
  onProgress({ phase: "tail", stopsDone: total, stopsTotal: total, detail: "Bumping offline-update timestamp…" });
  await importApi.bumpOffline(tourId);
  result.tail.offline = true;
  await journal.append(tourId, { type: "tail_done" });
  // Transcripts: each stop create fired the backend's background transcription.
  // Deliberately NO bulk transcribe call (see importApi header).

  onProgress({ phase: "verify", stopsDone: total, stopsTotal: total, detail: "Verifying every file on the CDN…" });
  result.verify = await verifyTour(tourId, audioLangs);
  await journal.append(tourId, { type: "verify", ok: result.verify.ok, failed: result.verify.failed.length });
  await journal.archive(tourId);
  onProgress({ phase: "done", stopsDone: total, stopsTotal: total, detail: "Import finished." });
  return result;
}

/** HEAD-check every file in the tour's live download manifest. */
export async function verifyTour(tourId: number, langs: string[]): Promise<VerifyResult> {
  const manifest = await importApi.downloadManifest(tourId, langs);
  const files: any[] = manifest?.files || [];
  const failed: string[] = [];
  let checked = 0;
  const queue = [...files];
  const workers = Array.from({ length: 4 }, async () => {
    while (queue.length) {
      const f = queue.shift();
      if (!f?.url) continue;
      const ok = await importApi.headOk(f.url);
      checked++;
      if (!ok) failed.push(f.url);
    }
  });
  await Promise.all(workers);
  const failedNonTranscribe = failed.filter((u) => !u.includes("/transcribe/"));
  return { ok: failedNonTranscribe.length === 0, checked, totalFiles: files.length, failed, failedNonTranscribe };
}

// =============================================================================
// Add audio languages to an EXISTING tour (shared logic: ./engine/addLanguages.js)
// =============================================================================

export interface AddLangRow {
  sl: number;
  stop: any; // sheet stop (names, lat/lng, audio file thunks)
  serverId: number;
  serverName: string;
  matchedBy: "name" | "location";
  existing: string[]; // languages that already have audio on the server
  add: string[]; // languages this run uploads
  skip: string[]; // selected languages already present
}

export interface AddLangPlan {
  rows: AddLangRow[];
  issues: any[];
  totals: { stops: number; uploads: number; skipped: number };
  langs: string[];
}

export interface AddLangResult {
  updated: number[]; // SLs whose stop row was updated
  unchanged: number[]; // SLs with nothing to add
  uploads: number;
  reusedUploads: number; // files found in the journal and still on the CDN
  tail: { route?: boolean; detail?: boolean; offline?: boolean };
  verify: VerifyResult | null;
  langs: string[]; // every audio language present on the tour after the run
}

const ADDLANG_SCOPE = "addlang";

/** Read-oriented preflight for add-languages mode. */
export async function preflightAddLanguages(tourId: number, model: any): Promise<{ tour: any; plan: AddLangPlan | null; issues: any[] }> {
  const issues: any[] = [];
  const push = (severity: string, code: string, message: string) => issues.push({ severity, code, message });

  const all = await importApi.getAllTours();
  const listed = (all || []).find((t: any) => t.id === tourId);
  if (!listed) {
    push("blocker", "tour-not-found", `Tour ${tourId} not found`);
    return { tour: null, plan: null, issues };
  }
  const tour = await importApi.getTour(tourId);
  if (!tour?.id) {
    push("blocker", "tour-not-found", `Tour ${tourId} not found`);
    return { tour: null, plan: null, issues };
  }
  const existing = tour.tourPointsDetail || [];
  if (!existing.length) {
    push("blocker", "tour-empty", `Tour has no stops yet — use "Create stops" mode for an empty tour`);
    return { tour, plan: null, issues };
  }
  if (await journal.hasActive(tourId, ADDLANG_SCOPE)) {
    push("info", "addlang-resume", "An earlier add-languages run on this tour did not finish — files it already uploaded are reused and languages already on the server are skipped");
  }
  const plan: AddLangPlan = planAddLanguages(model, existing);
  issues.push(...plan.issues);
  if (tour.published === 1 || tour.published === true) {
    push("info", "tour-live", "This tour is LIVE — the new languages reach users as soon as the finishing steps run (detail file refresh + offline update). The route is not regenerated (stops do not move).");
  }
  try {
    const tpl = typeof tour.bookingEmailTemplate === "string" ? JSON.parse(tour.bookingEmailTemplate) : tour.bookingEmailTemplate;
    if (Array.isArray(tpl) && tpl.length) {
      const langs = tpl.map((v: any) => v.language);
      for (const l of plan.langs) {
        if (!langs.includes(l)) push("warning", "template-missing-lang", `Booking email template has no "${l}" variant — add it in Edit Tour so ${l} bookings get their email`);
      }
    } else {
      push("warning", "no-email-template", "Tour has no booking email template yet — add it in Edit Tour before going live.");
    }
  } catch {
    push("warning", "template-unparseable", "Tour's booking email template is not valid JSON — check it in Edit Tour");
  }
  return { tour, plan, issues };
}

/**
 * Execute add-languages. Validation + preflight passed and the operator confirmed.
 * Idempotent and resumable: the plan is recomputed against the live server state
 * at every start (a language with a non-empty file on the server is "done"), and
 * journaled uploads are reused only while the file is still on the CDN.
 */
export async function runAddLanguages(
  model: any,
  tourId: number,
  onProgress: (p: Progress) => void,
  shouldAbort: () => boolean
): Promise<AddLangResult> {
  const result: AddLangResult = { updated: [], unchanged: [], uploads: 0, reusedUploads: 0, tail: {}, verify: null, langs: [] };

  const tour = await importApi.getTour(tourId);
  if (!tour?.id) throw new Error(`Tour ${tourId} not found`);
  const serverStops = tour.tourPointsDetail || [];
  if (!serverStops.length) throw new Error(`Tour ${tourId} has no stops — nothing to add audio to.`);

  const plan: AddLangPlan = planAddLanguages(model, serverStops);
  const blockers = plan.issues.filter((i: any) => i.severity === "blocker");
  if (blockers.length) throw new Error(`The tour's stops changed since the preview: ${blockers.map((b: any) => b.message).join("; ")}`);

  const total = plan.rows.length;
  const report = (p: Partial<Progress>) =>
    onProgress({ phase: "stop", stopsDone: result.updated.length + result.unchanged.length, stopsTotal: total, detail: "", ...p } as Progress);

  // Journal replay + CDN liveness check on reused filenames.
  report({ phase: "reconcile", detail: "Checking which languages already exist…" });
  const journalUploads = (await journal.summarize(tourId, ADDLANG_SCOPE)).uploads;
  for (const u of Object.values(journalUploads)) {
    for (const [lang, f] of Object.entries(u.audio)) {
      if (f && !(await importApi.headOk(`${CDN_URL}/audioUpload/${f}`))) delete u.audio[lang];
    }
  }

  for (const row of plan.rows) {
    if (shouldAbort()) throw new Error("Paused by operator — press Resume to continue (nothing is lost).");
    if (!row.add.length) {
      result.unchanged.push(row.sl);
      continue;
    }
    const prior = journalUploads[row.sl]?.audio || {};
    const uploaded: Record<string, string> = {};
    for (const lang of row.add) {
      if (shouldAbort()) throw new Error("Paused by operator — press Resume to continue (nothing is lost).");
      if (prior[lang]) {
        uploaded[lang] = prior[lang];
        result.reusedUploads++;
        continue;
      }
      const f = row.stop.audio[lang];
      const data = f.getData();
      if (!looksLikeMp3(data)) throw new Error(`Stop ${row.sl} ${lang}: "${f.entryName}" is not a valid MP3 — fix the zip, then Resume.`);
      report({ stopSl: row.sl, stopName: row.stop.names.en, detail: `Uploading ${lang} audio (${(data.length / 1e6).toFixed(1)} MB)…` });
      const serverName = await importApi.uploadAudio(fname(tourId, row.sl, `_${lang}.mp3`), data, (pct) =>
        report({ stopSl: row.sl, stopName: row.stop.names.en, detail: `Uploading ${lang} audio…`, uploadPct: pct })
      );
      await journal.append(tourId, { type: "upload", sl: row.sl, kind: "audio", lang, file: serverName }, ADDLANG_SCOPE);
      uploaded[lang] = serverName;
      result.uploads++;
    }

    // Merge against the stop's CURRENT map (fresh read-only fetch — a manual edit
    // during the run must not be overwritten), then PUT the full merged map.
    report({ stopSl: row.sl, stopName: row.stop.names.en, detail: `Saving ${row.add.join(", ")} on stop #${row.serverId}…` });
    const fresh = await importApi.getStop(row.serverId);
    if (!fresh?.id) throw new Error(`Stop #${row.serverId} (${row.stop.names.en}) disappeared from the server — refusing to continue.`);
    const payload = buildUpdatePayload(tourId, mergeLanguageMap(fresh.stopLanguagesMap, uploaded));
    try {
      await importApi.updateStop(row.serverId, payload);
    } catch (e) {
      if (!(e instanceof NetworkUncertainError)) throw e;
      // Uncertain outcome: re-read and only resend if the languages did not land.
      const again = await importApi.getStop(row.serverId);
      const present: string[] = presentLangs(again?.stopLanguagesMap);
      if (!row.add.every((l) => present.includes(l))) await importApi.updateStop(row.serverId, payload);
    }
    await journal.append(tourId, { type: "stop_updated", sl: row.sl, id: row.serverId, langs: row.add }, ADDLANG_SCOPE);
    result.updated.push(row.sl);
    report({ stopSl: row.sl, stopName: row.stop.names.en, detail: `Stop updated (${row.add.join(", ")}).` });
  }

  // Tail: no route regeneration (stops did not move) — detail refresh + offline bump,
  // exactly what the Stop List "Update" button does. If the CR-8 guard reports the
  // stored route stale (pre-existing condition), regenerate it once and retry.
  onProgress({ phase: "tail", stopsDone: total, stopsTotal: total, detail: "Refreshing tour detail file…" });
  const after = await importApi.getTour(tourId); // designed side effect: rewrites tour-details-{id}.json
  result.tail.detail = true;
  onProgress({ phase: "tail", stopsDone: total, stopsTotal: total, detail: "Bumping offline-update timestamp…" });
  try {
    await importApi.bumpOffline(tourId);
  } catch (e: any) {
    if (e?.status !== 409) throw e;
    onProgress({ phase: "tail", stopsDone: total, stopsTotal: total, detail: "Stored route was stale — regenerating walking route (Mapbox)…" });
    await importApi.generateRoute(tourId);
    result.tail.route = true;
    await importApi.getTour(tourId);
    await importApi.bumpOffline(tourId);
  }
  result.tail.offline = true;
  await journal.append(tourId, { type: "tail_done" }, ADDLANG_SCOPE);
  // Transcripts: each tourPoints/update fired the backend's background transcription
  // for the stop's files (skip-existing). No bulk transcribe call (see importApi header).

  const langsNow = new Set<string>();
  for (const sp of after?.tourPointsDetail || []) for (const l of presentLangs(sp.stopLanguagesMap) as string[]) langsNow.add(l);
  result.langs = LANG_ORDER.filter((l: string) => langsNow.has(l));

  onProgress({ phase: "verify", stopsDone: total, stopsTotal: total, detail: "Verifying every file on the CDN…" });
  result.verify = await verifyTour(tourId, result.langs);
  await journal.append(tourId, { type: "verify", ok: result.verify.ok, failed: result.verify.failed.length }, ADDLANG_SCOPE);
  await journal.archive(tourId, ADDLANG_SCOPE);
  onProgress({ phase: "done", stopsDone: total, stopsTotal: total, detail: "Languages added." });
  return result;
}
